import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const INSTALL_PROMPT_DISMISSED_KEY = 'installPromptDismissed';

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if user already dismissed the prompt in this session
    const wasDismissed = sessionStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    if (wasDismissed) {
      return;
    }

    // Check if iOS
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as unknown as { MSStream: string }).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // For iOS, show prompt immediately after delay since there's no beforeinstallprompt event
    if (isIOSDevice) {
      setTimeout(() => setShowPrompt(true), 5000);
      return;
    }

    // For other platforms, wait for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      // Store in sessionStorage so we don't show again even if accepted
      sessionStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'true');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store dismissal in sessionStorage to prevent showing again in this session
    sessionStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'true');
  };

  if (!showPrompt) return null;

  // iOS requires manual installation
  if (isIOS) {
    return (
      <Alert
        variant="info"
        className="position-fixed bottom-0 start-0 end-0 m-3 mb-5"
        style={{ zIndex: 1050 }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <strong>{t('install.iosTitle')}</strong>
            <p className="mb-0 small mt-1">{t('install.iosInstructions')}</p>
          </div>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-dark"
            onClick={handleDismiss}
          >
            <X size={20} />
          </Button>
        </div>
      </Alert>
    );
  }

  // Android/Desktop Chrome
  return (
    <Alert
      variant="primary"
      className="position-fixed bottom-0 start-0 end-0 m-3 mb-5"
      style={{ zIndex: 1050 }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <Download size={24} />
          <div>
            <strong>{t('install.title')}</strong>
            <p className="mb-0 small">{t('install.description')}</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={handleDismiss}>
            {t('install.later')}
          </Button>
          <Button variant="primary" size="sm" onClick={handleInstall}>
            {t('install.install')}
          </Button>
        </div>
      </div>
    </Alert>
  );
}
